import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { sesionCajaService, SesionCaja, SesionCajaCierre } from '../../services/sesionCajaService';

interface CerrarSesionFormProps {
  sesion: SesionCaja;
  onClose: () => void;
}

const CerrarSesionForm = ({ sesion, onClose }: CerrarSesionFormProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<SesionCajaCierre>({
    defaultValues: {
      montoCierre: sesion.montoCierre || sesion.montoEfectivoEsperado,
      montoEfectivoReal: sesion.montoEfectivoReal || sesion.montoEfectivoEsperado
    }
  });

  const montoEfectivoReal = watch('montoEfectivoReal');
  const montoEfectivoEsperado = sesion.montoEfectivoEsperado || (Number(sesion.montoApertura) + Number(sesion.totalEfectivo));
  const diferencia = montoEfectivoReal ? Number(montoEfectivoReal) - Number(montoEfectivoEsperado) : 0;

  useEffect(() => {
    reset({
      montoCierre: sesion.montoCierre || montoEfectivoEsperado,
      montoEfectivoReal: sesion.montoEfectivoReal || montoEfectivoEsperado
    });
  }, [sesion, montoEfectivoEsperado, reset]);

  const cerrarMutation = useMutation(
    (data: SesionCajaCierre) => sesionCajaService.cerrar(sesion.id, data),
    {
      onSuccess: () => {
        toast.success('Sesión cerrada correctamente');
        queryClient.invalidateQueries(['sesiones-caja']);
        queryClient.invalidateQueries(['ordenes']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al cerrar sesión');
      }
    }
  );

  const onSubmit = (data: SesionCajaCierre) => {
    cerrarMutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Cerrar Sesión de Caja</h2>

        <div className="mb-4 p-4 bg-gray-50 rounded">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Caja:</p>
              <p className="font-medium">{sesion.caja?.name}</p>
            </div>
            <div>
              <p className="text-gray-600">Total Ventas:</p>
              <p className="font-medium">${Number(sesion.totalVentas).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-600">Efectivo:</p>
              <p className="font-medium">${Number(sesion.totalEfectivo).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-600">Tarjeta:</p>
              <p className="font-medium">${Number(sesion.totalTarjeta).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-600">Monto Apertura:</p>
              <p className="font-medium">${Number(sesion.montoApertura).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-600">Efectivo Esperado:</p>
              <p className="font-medium">${Number(montoEfectivoEsperado).toFixed(2)}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto de Cierre
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('montoCierre', { 
                valueAsNumber: true,
                min: { value: 0, message: 'Debe ser mayor o igual a 0' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.montoCierre && (
              <p className="text-red-500 text-sm mt-1">{errors.montoCierre.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Efectivo Real *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('montoEfectivoReal', { 
                required: 'Efectivo real es requerido',
                valueAsNumber: true,
                min: { value: 0, message: 'Debe ser mayor o igual a 0' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.montoEfectivoReal && (
              <p className="text-red-500 text-sm mt-1">{errors.montoEfectivoReal.message}</p>
            )}
            {montoEfectivoReal && (
              <p className={`text-sm mt-1 ${diferencia !== 0 ? (diferencia > 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-600'}`}>
                Diferencia: {diferencia > 0 ? '+' : ''}${diferencia.toFixed(2)}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              {...register('notas')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Notas sobre el cierre..."
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={cerrarMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {cerrarMutation.isLoading ? 'Cerrando...' : 'Cerrar Sesión'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CerrarSesionForm;

